import { useState, useCallback, useMemo, useRef } from 'react';

import { saveAs } from 'file-saver';
import { CalendarIcon, TrafficConeIcon } from 'lucide-react';
import Papa from 'papaparse';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTitle } from 'react-use';
import { toast } from 'sonner';

import { BrainVersionSelector } from '@/components/atoms/BrainVersionSelector';
import FileDollarIcon from '@/components/atoms/Icons/FileDollarIcon';
import { useStepWizard } from '@/components/organisms/StepWizard/StepWizard';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { EventName, PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { AudiencePanel } from './components/AudiencePanel';
import { ContinueAnywayPopover } from './components/ContinueAnywayPopover';
import { useUnifiedSetupForm } from './hooks/useUnifiedSetupForm';
import { resolveContinueGateDecision, resolveSaveAndExitDecision } from './setupGate';
import { CampaignEditor } from '../../components/CampaignEditor';
import { MatchFieldsModal } from '../../components/modals/MatchFieldsModal';
import { csvSampleData, getDebtCampaignVariables, getDebtCsvSampleData } from '../../constants';
import { useAudienceUpload } from '../../hooks/useAudienceUpload';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import { useRegisterWizardStep } from '../../hooks/useWizardStepController';
import {
  selectCsvMappings,
  selectDraftAudienceStats,
  selectDraftSubscribers,
} from '../../redux/selectors';
import {
  getApiSubscribersStats,
  getAudienceStats,
  getCampaignContextVariableOptions,
  getDraftContextVariableOptions,
  getExtraRequiredFields,
  showUnmatchedRequiredToast,
} from '../../utils';
import { GOAL_MAX_LENGTH } from '../../validator';
import {
  CampaignNameField,
  ComplianceProfileField,
  TagsField,
} from '../payment-recovery/components';
import { useUnmatchedFields } from '../payment-recovery/hooks/useUnmatchedFields';
import { AGENT_GOAL_OPTIONS, AgentGoal } from '../payment-recovery/useBuildDebtCampaignLogic';

const GOAL_ICONS: Record<AgentGoal, React.ComponentType<{ className?: string }>> = {
  promise_to_pay: CalendarIcon,
  claim_of_payment: FileDollarIcon,
  secure_confirm_payment: TrafficConeIcon,
};

export const UnifiedSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const campaignId = useSelector(selectCampaignId);
  const subscribers = useSelector(selectDraftSubscribers);
  const draftAudienceStats = useSelector(selectDraftAudienceStats);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.WRITE)
  );

  // Campaign API hooks
  const { campaign, updateCampaignAsync, verifySubscribersAsync } = useCampaigns(campaignId);

  const { bulkUpsertSubscribersAsync } = useCampaignSubscribers();
  const { campaign_subscribers: apiSubscribers } = useCampaignSubscribers(campaignId);
  // Use draft subscribers (current upload session) if available, otherwise fall back to API subscribers
  const variableOptions = useMemo(
    () =>
      subscribers.length > 0
        ? getDraftContextVariableOptions(subscribers)
        : getCampaignContextVariableOptions(apiSubscribers || []),
    [subscribers, apiSubscribers]
  );

  // Local State
  const [hasUserDeletedAudience, setHasUserDeletedAudience] = useState(false);
  const [shouldReplaceSubscribers, setShouldReplaceSubscribers] = useState(false);

  // "Continue anyway" (Continue gate) and "Save anyway" (Save & Exit gate) popovers, each anchored
  // to its own header button. The ref carries the Continue confirm into the wizard's re-run gate.
  const { goToNext, primaryActionRef, secondaryActionRef } = useStepWizard();
  const [continueAnywayOpen, setContinueAnywayOpen] = useState(false);
  const [saveAnywayOpen, setSaveAnywayOpen] = useState(false);
  const continueAnywayConfirmedRef = useRef(false);

  // Custom Hooks
  const {
    form,
    draftCampaignName,
    allTagOptions,
    handleCampaignNameChange,
    handleComplianceProfileSelect,
    handleTagsChange,
    handleCreateNewTag,
    handleBrainSubmit,
    saveCampaignData,
    // Unified-specific
    campaignType,
    autonomousActions,
    goal,
    outreachGoal,
    handleAutonomousActionsChange,
    handleGoalChange,
    handleOutreachGoalChange,
  } = useUnifiedSetupForm({
    campaignId,
    campaign,
    apiSubscribers,
    hasUserDeletedAudience,
    shouldReplaceSubscribers,
    updateCampaignAsync,
    bulkUpsertSubscribersAsync,
  });
  const aiAgent = useWatch({ control: form.control, name: 'aiAgent' });
  const aiAgentVersion = useWatch({ control: form.control, name: 'aiAgentVersion' });

  const isPaymentRecovery = campaignType === 'payment_recovery';

  // residence_state is conditionally required (FDCPA — US/BR); feeds the matcher and the gate below.
  const extraRequiredFields = useMemo(() => {
    const source = subscribers.length > 0 ? subscribers : (apiSubscribers ?? []);
    return getExtraRequiredFields(source);
  }, [subscribers, apiSubscribers]);

  // Payment-recovery gate: Continue stays blocked until every required field is matched (mapping-based, live-upload only).
  const csvMappings = useSelector(selectCsvMappings);
  const { hasRequiredUnmatched } = useUnmatchedFields({
    csvMappings: csvMappings ?? null,
    isShowingReduxData: subscribers.length > 0,
    extraRequiredFields,
  });

  // Audience delete handler for saved campaigns
  const handleAudienceDelete = useCallback(async () => {
    if (campaignId) {
      await Promise.all([
        updateCampaignAsync({
          campaign_id: campaignId,
          metadata: {
            ...(campaign?.metadata || {}),
            email_subscribers: undefined,
            phone_subscribers: undefined,
            total_amount_due: undefined,
            variables_count: undefined,
          },
        }),
        bulkUpsertSubscribersAsync({ campaign_id: campaignId, subscribers: [], replace: true }),
      ]);
    }
  }, [campaignId, campaign?.metadata, updateCampaignAsync, bulkUpsertSubscribersAsync]);

  const {
    selectedFile,
    fileError,
    isProcessing,
    matchFieldsModalOpen,
    parsedCsvData,
    savedMappings,
    savedLiveInstructionTargets,
    skipAutoSuggest,
    subscribers: uploadedSubscribers,
    handleFileSelect,
    handleMappingsConfirm,
    handleAddSubscriber,
    handleMatchMissingFields,
    handleModalOpenChange,
    isQuickAddSource,
  } = useAudienceUpload({
    campaignId,
    campaign,
    campaignType,
    apiSubscribers,
    hasUserDeletedAudience,
    setHasUserDeletedAudience,
    setShouldReplaceSubscribers,
    verifySubscribersAsync,
    form,
    onAudienceDelete: handleAudienceDelete,
  });

  useTitle(t('pages.campaigns_setup', { 0: draftCampaignName || campaign?.name || '-' }));

  useSegments(PageName.CAMPAIGN_SETUP);

  // Download sample CSV
  const handleDownloadSample = useCallback(() => {
    trackEvent(EventName.CampaignDownloadSample, {
      campaign_id: campaignId,
      campaign_type: campaignType,
    });
    const sampleData = isPaymentRecovery ? getDebtCsvSampleData() : csvSampleData;
    const csvContent = Papa.unparse(sampleData);
    const file = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(file, t('campaigns.setup.sample_filename'));
  }, [t, isPaymentRecovery, campaignId, campaignType]);

  // Confirm → mark the decision and re-run the wizard gate (now past the check).
  const handleContinueAnyway = useCallback(() => {
    continueAnywayConfirmedRef.current = true;
    setContinueAnywayOpen(false);
    void goToNext();
  }, [goToNext]);

  // Forward gate for the wizard: validate + persist the draft, returning whether
  // we may advance to messaging. The wizard owns the navigation on success.
  const validateAndSave = useCallback(async (): Promise<boolean> => {
    // Skip on the confirm re-run so one "continue anyway" isn't double-counted.
    if (!continueAnywayConfirmedRef.current) {
      trackEvent(EventName.CampaignSetupContinue, {
        campaign_id: campaignId,
        campaign_type: campaignType,
        has_audience: subscribers.length > 0,
        autonomous_actions: !!autonomousActions,
        subscribers_count: subscribers.filter((s) => !s.error).length,
      });
    }
    // Set the inline "required" errors for every missing field first, so each
    // field shows its message even when the match-fields toast also fires below.
    let formInvalid = false;

    if (!(await form.trigger('name'))) {
      formInvalid = true;
    }

    // Require AI agent
    if (!form.getValues('aiAgent')) {
      form.setError('aiAgent', { type: 'manual', message: t('campaigns.setup.ai_agent_required') });
      formInvalid = true;
    }

    // Require goal and compliance profile only when autonomous actions is enabled
    if (autonomousActions) {
      if (isPaymentRecovery) {
        const complianceProfile = form.getValues('complianceProfile');
        if (!complianceProfile) {
          form.setError('complianceProfile', {
            type: 'manual',
            message: t('campaigns.setup.compliance_profile_required'),
          });
          formInvalid = true;
        }
        if (!goal) {
          form.setError('goal', { type: 'manual', message: t('campaigns.setup.goal_required') });
          formInvalid = true;
        }
      } else if (!outreachGoal.trim()) {
        form.setError('outreachGoal', {
          type: 'manual',
          message: t('campaigns.setup.goal_required'),
        });
        formInvalid = true;
      }
    }

    // Routing extracted to resolveContinueGateDecision (unit-tested).
    const errorCount = subscribers.filter((s) => !!s.error).length;
    const hasPartialErrors = errorCount > 0 && errorCount < subscribers.length;
    const decision = resolveContinueGateDecision({
      isPaymentRecovery,
      hasRequiredUnmatched,
      formInvalid,
      hasPartialErrors,
      continueConfirmed: continueAnywayConfirmedRef.current,
    });
    if (decision === 'block-unmatched') {
      showUnmatchedRequiredToast(t, handleMatchMissingFields);
      return false;
    }
    if (decision === 'block-invalid') {
      return false;
    }
    if (decision === 'confirm-continue') {
      setContinueAnywayOpen(true);
      return false;
    }
    // Proceed — consume a pending "continue anyway" confirmation so it isn't reused.
    if (hasPartialErrors && continueAnywayConfirmedRef.current) {
      continueAnywayConfirmedRef.current = false;
    }
    const saved = await saveCampaignData(true);
    return !!saved;
  }, [
    form,
    saveCampaignData,
    t,
    isPaymentRecovery,
    autonomousActions,
    outreachGoal,
    goal,
    campaignId,
    campaignType,
    subscribers,
    hasRequiredUnmatched,
    handleMatchMissingFields,
  ]);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => e.preventDefault(), []);

  // The actual save + leave, shared by the direct Save & Exit path and the "Save anyway" confirm.
  const doSaveAndExit = useCallback(async () => {
    const saved = await saveCampaignData(false);
    if (saved) {
      navigate(`/${slug}/campaigns`);
    }
  }, [saveCampaignData, navigate, slug]);

  // Confirm on the "Save anyway" popover → drop the errored rows and leave.
  const handleSaveAnyway = useCallback(() => {
    setSaveAnywayOpen(false);
    void doSaveAndExit();
  }, [doSaveAndExit]);

  const handleSaveAndExit = useCallback(async () => {
    trackEvent(EventName.CampaignSetupSaveAndExit, {
      campaign_id: campaignId,
      campaign_type: campaignType,
    });
    if (!(await form.trigger('name'))) {
      return;
    }
    // Routing extracted to resolveSaveAndExitDecision (unit-tested).
    const errorCount = subscribers.filter((s) => !!s.error).length;
    const hasPartialErrors = errorCount > 0 && errorCount < subscribers.length;
    const decision = resolveSaveAndExitDecision({
      isPaymentRecovery,
      hasRequiredUnmatched,
      hasPartialErrors,
    });
    if (decision === 'block-unmatched') {
      const toastId = toast.warning(t('campaigns.setup.audience_not_saved_unmatched'), {
        action: (
          <Button
            variant="primary"
            className="h-auto min-w-0 shrink-0 px-2 py-1"
            onClick={() => {
              toast.dismiss(toastId);
              handleMatchMissingFields();
            }}
          >
            {t('campaigns.setup.match_fields')}
          </Button>
        ),
        classNames: { toast: '!w-128 !items-center !gap-4 !p-4', icon: '!mt-0.5 !self-start' },
      });
      return;
    }
    if (decision === 'confirm-save-anyway') {
      setSaveAnywayOpen(true);
      return;
    }
    await doSaveAndExit();
  }, [
    form,
    doSaveAndExit,
    subscribers,
    campaignId,
    campaignType,
    isPaymentRecovery,
    hasRequiredUnmatched,
    handleMatchMissingFields,
    t,
  ]);

  const handleReviewAudienceClick = useCallback(() => {
    if (!campaignId) {
      return;
    }
    navigate(`/${slug}/campaigns/${campaignId}/audience`);
  }, [campaignId, slug, navigate]);

  // Register this step's controller with the wizard. Save & Exit keeps its own
  // navigation; the forward gate (validate) is the wizard's responsibility.
  useRegisterWizardStep({ validate: validateAndSave, saveAndExit: handleSaveAndExit });

  // Memoize available variables for MatchFieldsModal
  const availableVariables = useMemo(
    () => (isPaymentRecovery ? getDebtCampaignVariables() : undefined),
    [isPaymentRecovery]
  );

  // Audience stats for outreach
  const outreachAudienceStats = useMemo(() => {
    if (isPaymentRecovery) {
      return undefined;
    }
    // Priority 1: Stats from current upload session
    if (draftAudienceStats) {
      return {
        total_subscribers: subscribers.filter((s) => !s.error).length,
        email_subscribers: draftAudienceStats.email_subscribers,
        phone_subscribers: draftAudienceStats.phone_subscribers,
        variables_count: draftAudienceStats.variables_count,
      };
    }
    // Priority 2: Stats from draft subscribers in Redux
    if (subscribers.length > 0) {
      const stats = getAudienceStats(subscribers);
      return { total_subscribers: subscribers.filter((s) => !s.error).length, ...stats };
    }
    // Priority 3: Stats from saved campaign metadata (on refresh)
    if (campaign?.metadata) {
      const meta = campaign.metadata as Record<string, number>;
      if (meta.email_subscribers != null || meta.phone_subscribers != null) {
        return {
          total_subscribers: campaign.subscribers_count || 0,
          email_subscribers: meta.email_subscribers || 0,
          phone_subscribers: meta.phone_subscribers || 0,
          variables_count: meta.variables_count || 0,
        };
      }
    }
    // Priority 4: Compute from saved subscribers (e.g. duplicated campaigns,
    // whose metadata stats aren't persisted by the create endpoint).
    if (apiSubscribers?.length) {
      return getApiSubscribersStats(apiSubscribers, campaign?.subscribers_count);
    }
    // Priority 5: Only total count available
    if (campaign?.subscribers_count) {
      return {
        total_subscribers: campaign.subscribers_count,
        email_subscribers: 0,
        phone_subscribers: 0,
        variables_count: 0,
      };
    }
    return undefined;
  }, [isPaymentRecovery, draftAudienceStats, subscribers, campaign, apiSubscribers]);

  return (
    <>
      <Form {...form}>
        <div className="flex w-full gap-4 rounded-xl bg-muted p-1">
          {/* Left panel - Campaign details */}
          <form
            className="min-w-0 flex-1 rounded-lg border border-border bg-background p-8"
            onSubmit={handleSubmit}
          >
            {/* Campaign Name */}
            <CampaignNameField
              form={form}
              canWrite={canWrite}
              onNameChange={handleCampaignNameChange}
            />

            {/* AI Agent */}
            <FormField
              control={form.control}
              name="aiAgent"
              render={() => (
                <FormItem className="mt-6">
                  <FormLabel>{t('campaigns.setup.ai_agent')}</FormLabel>
                  <BrainVersionSelector
                    onSelect={handleBrainSubmit}
                    initialBrainId={aiAgent || undefined}
                    initialVersion={aiAgentVersion}
                    disabled={!canWrite}
                    placeholder={t('common.select_dots')}
                  />
                  <FormDescription>{t('campaigns.setup.ai_agent_description')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* AI Capabilities - Autonomous Actions */}
            <div className="mt-6 space-y-2">
              <Label className="text-sm font-medium">{t('campaigns.setup.ai_capabilities')}</Label>
              <Switch
                id="autonomous-actions"
                variant="choice-card"
                switchPosition="right"
                label={t('campaigns.setup.autonomous_actions')}
                description={t('campaigns.setup.autonomous_actions_description')}
                checked={!!autonomousActions}
                onCheckedChange={handleAutonomousActionsChange}
                disabled={!canWrite}
              />
              {autonomousActions && (
                <FormField
                  control={form.control}
                  name="evaluateFirstMessage"
                  render={({ field }) => (
                    <label
                      htmlFor="evaluate-first-message"
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4"
                    >
                      <Checkbox
                        id="evaluate-first-message"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                        disabled={!canWrite}
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-sm leading-none font-medium">
                          {t('campaigns.setup.evaluate_first_message')}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          {t('campaigns.setup.evaluate_first_message_description')}
                        </p>
                      </div>
                    </label>
                  )}
                />
              )}
            </div>

            {/* AI Agent's Goal (free-text) - for outreach with autonomous actions */}
            {!isPaymentRecovery && autonomousActions && (
              <FormField
                control={form.control}
                name="outreachGoal"
                render={() => (
                  <FormItem className="mt-6">
                    <div className="flex items-center justify-between">
                      <FormLabel>{t('campaigns.setup.goal_label')}</FormLabel>
                      <span
                        className={cn(
                          'text-xs text-muted-foreground',
                          outreachGoal.length > GOAL_MAX_LENGTH && 'text-destructive'
                        )}
                      >
                        {outreachGoal.length}/{GOAL_MAX_LENGTH}
                      </span>
                    </div>
                    <CampaignEditor
                      content={outreachGoal}
                      onContentChange={(value) => {
                        handleOutreachGoalChange(value);
                        form.clearErrors('outreachGoal');
                      }}
                      variableOptions={variableOptions}
                      placeholder={t('campaigns.setup.goal_placeholder')}
                      className="scrollable min-h-24"
                      placeholderWhenEmptyOnly
                      emptySuggestionText={t('campaigns.setup.goal_no_variables')}
                      editable={canWrite}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* AI Agent's Goal (dropdown) - only for payment recovery with autonomous actions */}
            {isPaymentRecovery && autonomousActions && (
              <FormField
                control={form.control}
                name="goal"
                render={() => (
                  <FormItem className="mt-6">
                    <FormLabel>{t('campaigns.setup.goal_label')}</FormLabel>
                    <Select
                      value={goal}
                      onValueChange={(value) => {
                        handleGoalChange(value);
                        form.clearErrors('goal');
                      }}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder={t('common.select_dots')}>
                          {goal && (
                            <span>
                              {t(AGENT_GOAL_OPTIONS.find((o) => o.value === goal)?.labelKey ?? '')}
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="w-md">
                        {AGENT_GOAL_OPTIONS.map((option) => {
                          const Icon = GOAL_ICONS[option.value];
                          const isDisabled = 'disabled' in option && option.disabled;
                          return (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                              disabled={isDisabled}
                              className="py-3 pl-3 data-[state=checked]:bg-muted [&>span:first-child]:hidden"
                            >
                              <div className="flex items-start gap-4">
                                <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                                <div className="flex flex-col gap-1">
                                  <span className="leading-none font-medium">
                                    {t(option.labelKey)}
                                  </span>
                                  <span className="text-sm leading-5 text-muted-foreground">
                                    {t(option.descriptionKey)}
                                  </span>
                                </div>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Compliance Profile - only for payment recovery with autonomous actions */}
            {isPaymentRecovery && autonomousActions && (
              <ComplianceProfileField
                form={form}
                canWrite={canWrite}
                onSelect={handleComplianceProfileSelect}
              />
            )}

            {/* Tags */}
            <TagsField
              form={form}
              canWrite={canWrite}
              allTagOptions={allTagOptions}
              onTagsChange={handleTagsChange}
              onCreateNewTag={handleCreateNewTag}
            />
          </form>

          {/* Right panel - Audience */}
          <AudiencePanel
            canWrite={canWrite}
            campaignType={campaignType}
            selectedFile={selectedFile}
            fileError={fileError}
            audienceCount={campaign?.subscribers_count || uploadedSubscribers.length}
            showDownloadSample={
              !uploadedSubscribers.length && (hasUserDeletedAudience || !apiSubscribers?.length)
            }
            onFileSelect={handleFileSelect}
            onDownloadSample={handleDownloadSample}
            campaign={campaign}
            hasUserDeletedAudience={hasUserDeletedAudience}
            onMatchMissingFields={handleMatchMissingFields}
            onReviewAudience={handleReviewAudienceClick}
            apiSubscribers={apiSubscribers}
            onAddSubscriber={handleAddSubscriber}
            existingSubscribers={uploadedSubscribers}
            audienceStats={outreachAudienceStats}
            hasErrors={subscribers.some((s) => !!s.error)}
            // Required fields unmatched → all rows read invalid; suppress the
            // "upload a new file" error so the panel's Match fields guidance shows.
            audienceError={
              isPaymentRecovery && hasRequiredUnmatched
                ? undefined
                : form.formState.errors.audience?.message
            }
            isProcessing={isProcessing}
            isQuickAddSource={isQuickAddSource}
          />
        </div>
      </Form>

      {/* Column Mapping Modal (payment recovery) */}
      {parsedCsvData && isPaymentRecovery && availableVariables && (
        <MatchFieldsModal
          open={matchFieldsModalOpen}
          onOpenChange={handleModalOpenChange}
          csvHeaders={parsedCsvData.headers}
          sampleRows={parsedCsvData.rows}
          availableVariables={availableVariables}
          initialMappings={savedMappings ?? undefined}
          initialLiveInstructionTargets={savedLiveInstructionTargets}
          skipAutoSuggest={skipAutoSuggest}
          extraRequiredFields={extraRequiredFields}
          onConfirm={handleMappingsConfirm}
        />
      )}

      <ContinueAnywayPopover
        open={continueAnywayOpen}
        onOpenChange={setContinueAnywayOpen}
        onConfirm={handleContinueAnyway}
        anchorRef={primaryActionRef}
      />

      <ContinueAnywayPopover
        open={saveAnywayOpen}
        onOpenChange={setSaveAnywayOpen}
        onConfirm={handleSaveAnyway}
        confirmLabel={t('campaigns.setup.save_anyway_confirm')}
        anchorRef={secondaryActionRef}
      />
    </>
  );
};
